import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calculator, TrendingUp, ArrowRight, Sparkles } from "lucide-react";

interface ROICalculatorProps {
  onCTA?: () => void;
  planPrice?: number; // monthly price to compare against
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const ROICalculator = ({ onCTA, planPrice = 79 }: ROICalculatorProps) => {
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [overdueInvoices, setOverdueInvoices] = useState(5);
  const [avgInvoice, setAvgInvoice] = useState(450);
  const [missedLeads, setMissedLeads] = useState(8);
  const [avgJob, setAvgJob] = useState(350);

  const result = useMemo(() => {
    const adminSavings = hoursPerWeek * hourlyRate * 4.33; // weekly → monthly
    // Assume AutoPilot recovers 60% of overdue and converts 35% of missed leads
    const collectionsRecovered = overdueInvoices * avgInvoice * 0.6;
    const leadsRecovered = missedLeads * avgJob * 0.35;
    const totalMonthly = adminSavings + collectionsRecovered + leadsRecovered;
    const yearly = totalMonthly * 12;
    const multiple = totalMonthly / Math.max(planPrice, 1);
    return { adminSavings, collectionsRecovered, leadsRecovered, totalMonthly, yearly, multiple };
  }, [hoursPerWeek, hourlyRate, overdueInvoices, avgInvoice, missedLeads, avgJob, planPrice]);

  return (
    <Card className="border-primary/30 shadow-2xl shadow-primary/10 overflow-hidden">
      <CardHeader className="bg-gradient-to-br from-primary/10 via-card to-accent/5 border-b border-border">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="secondary" className="bg-primary/15 text-primary border-primary/20">
            <Calculator className="h-3 w-3 mr-1" /> ROI Calculator
          </Badge>
        </div>
        <CardTitle className="text-2xl md:text-3xl">See what Detail Pro is worth to you.</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Three honest inputs. Real monthly impact. No fluff.
        </p>
      </CardHeader>
      <CardContent className="p-6 md:p-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Hours/week you spend on admin</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" min={0} value={hoursPerWeek} onChange={(e) => setHoursPerWeek(Number(e.target.value) || 0)} />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">@ $</span>
                  <Input type="number" min={0} value={hourlyRate} onChange={(e) => setHourlyRate(Number(e.target.value) || 0)} />
                  <span className="text-xs text-muted-foreground">/hr</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Overdue invoices per month</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" min={0} value={overdueInvoices} onChange={(e) => setOverdueInvoices(Number(e.target.value) || 0)} />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">avg $</span>
                  <Input type="number" min={0} value={avgInvoice} onChange={(e) => setAvgInvoice(Number(e.target.value) || 0)} />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Leads you miss per month</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" min={0} value={missedLeads} onChange={(e) => setMissedLeads(Number(e.target.value) || 0)} />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">avg job $</span>
                  <Input type="number" min={0} value={avgJob} onChange={(e) => setAvgJob(Number(e.target.value) || 0)} />
                </div>
              </div>
            </div>
          </div>

          {/* Output */}
          <div className="rounded-xl bg-gradient-to-br from-primary/15 via-primary/5 to-accent/10 border border-primary/20 p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-primary mb-2">
                <TrendingUp className="h-3.5 w-3.5" /> Estimated monthly impact
              </div>
              <div className="text-4xl md:text-5xl font-bold tracking-tight">
                {fmt(result.totalMonthly)}
                <span className="text-base font-normal text-muted-foreground">/mo</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                ≈ <span className="font-semibold text-foreground">{fmt(result.yearly)}</span> per year
              </div>

              <div className="mt-5 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Admin time reclaimed</span>
                  <span className="font-medium">{fmt(result.adminSavings)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Collections recovered</span>
                  <span className="font-medium">{fmt(result.collectionsRecovered)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Leads converted</span>
                  <span className="font-medium">{fmt(result.leadsRecovered)}</span>
                </div>
              </div>

              <div className="mt-5 rounded-lg bg-background/60 backdrop-blur border border-border p-3">
                <div className="text-xs text-muted-foreground">Detail Pro pays for itself</div>
                <div className="text-xl font-bold text-primary">
                  {result.multiple >= 1 ? `${result.multiple.toFixed(1)}× over` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  vs ${planPrice}/mo Professional plan
                </div>
              </div>
            </div>

            {onCTA && (
              <Button className="w-full mt-6 group" size="lg" onClick={onCTA}>
                <Sparkles className="h-4 w-4 mr-2" />
                Start free — capture this every month
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ROICalculator;
